import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "twelve12_install_dismissed";

export const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS] = useState(() =>
    typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent)
  );

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // Affiche la bannière après quelques secondes même sans événement natif
    // (instructions manuelles pour iOS / navigateurs sans beforeinstallprompt)
    const t = setTimeout(() => setVisible(true), 4000);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onPrompt);
    };
  }, [isIOS]);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          data-testid="install-banner"
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-40 bg-coal border border-white/15 p-4 flex items-center gap-4 shadow-2xl shadow-black/60"
        >
          <img src="/icon-192.png" alt="TWELVE 12-SHOP" className="w-12 h-12 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-syne font-bold uppercase tracking-tight text-sm">Installer l'app</p>
            <p className="font-mono text-[10px] text-ash uppercase tracking-[0.1em] mt-0.5 leading-relaxed">
              {isIOS ? (
                <>
                  <Share size={10} className="inline -mt-0.5" /> Partager → « Sur l'écran d'accueil »
                </>
              ) : deferredPrompt ? (
                "Accès direct depuis votre écran d'accueil"
              ) : (
                "Menu ⋮ du navigateur → « Ajouter à l'écran d'accueil »"
              )}
            </p>
          </div>
          {!isIOS && deferredPrompt && (
            <button
              data-testid="install-banner-button"
              onClick={install}
              className="shrink-0 bg-blood text-white font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 flex items-center gap-2 hover:bg-white hover:text-ink transition-colors"
            >
              <Download size={12} /> Installer
            </button>
          )}
          <button
            data-testid="install-banner-close"
            onClick={dismiss}
            aria-label="Fermer"
            className="shrink-0 p-1.5 text-ash hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
